import type { RouteRecordRaw } from 'vue-router'

// Every master is a child of the `masters` layout route, so the left rail stays
// mounted and each one gets its own deep-linkable URL
// (/hr/user-management/masters/<master>). The bare top-level paths that these
// used to live at are kept below as redirects so existing links -- the Admin
// mega-menu in hrMenuConfig.ts, bookmarks -- continue to resolve.
export const userManagementRoutes: RouteRecordRaw[] = [
  {
    path: 'user-management',
    component: () => import('@/modules/hr/UserManagement/layout/UserManagementLayout.vue'),
    meta: { title: 'User Management', requiresAuth: true },
    children: [
      {
        path: '',
        redirect: { name: 'UserManagementMasters' }
      },
      {
        path: 'masters',
        component: () => import('@/modules/hr/UserManagement/masters/UserManagementMaster.vue'),
        meta: { title: 'Masters', requiresAuth: true, breadcrumb: 'Masters' },
        children: [
          {
            path: '',
            redirect: { name: 'UserManagementMasters' }
          },

          // --- Organisation structure ---
          {
            path: 'organization',
            name: 'UserManagementMasters',
            component: () => import('@/modules/hr/UserManagement/masters/organisation/components/OrganisationList.vue'),
            meta: { title: 'Organization', requiresAuth: true, breadcrumb: 'Organization' }
          },
          {
            path: 'branch',
            name: 'MasterBranch',
            component: () => import('@/modules/hr/UserManagement/masters/branch/components/BranchList.vue'),
            meta: { title: 'Branch', requiresAuth: true, breadcrumb: 'Branch' }
          },
          {
            path: 'department',
            name: 'MasterDepartment',
            component: () => import('@/modules/hr/UserManagement/masters/department/components/DepartmentList.vue'),
            meta: { title: 'Department', requiresAuth: true, breadcrumb: 'Department' }
          },
          {
            path: 'sub-department',
            name: 'MasterSubDepartment',
            component: () => import('@/modules/hr/UserManagement/masters/subDepartment/components/SubDepartmentList.vue'),
            meta: { title: 'Sub-Department', requiresAuth: true, breadcrumb: 'Sub-Department' }
          },

          // --- Access ---
          {
            path: 'roles',
            name: 'MasterRoles',
            component: () => import('@/modules/hr/UserManagement/rolesAndPermissions/components/RoleList.vue'),
            meta: { title: 'Roles and Permissions', requiresAuth: true, breadcrumb: 'Roles and Permissions' }
          },
          {
            path: 'users',
            name: 'MasterUsers',
            component: () => import('@/modules/hr/UserManagement/user/components/UserList.vue'),
            meta: { title: 'Users', requiresAuth: true, breadcrumb: 'Users' }
          },

          // --- Configuration ---
          {
            path: 'location-master',
            name: 'LocationMasterList',
            component: () => import('@/modules/hr/UserManagement/masters/locationMaster/components/LocationMasterList.vue'),
            meta: { title: 'Location Master', requiresAuth: true, breadcrumb: 'Location Master' }
          },
          {
            path: 'location-type',
            name: 'LocationTypeList',
            component: () => import('@/modules/hr/UserManagement/masters/locationType/components/LocationTypeList.vue'),
            meta: { title: 'LocationType Master', requiresAuth: true, breadcrumb: 'LocationType Master' }
          },
          {
            path: 'approval',
            name: 'ApprovalList',
            component: () => import('@/modules/hr/UserManagement/masters/approval/components/ApprovalList.vue'),
            meta: { title: 'Approval', requiresAuth: true, breadcrumb: 'Approval' }
          },
          {
            path: 'rule-master',
            name: 'RuleMasterList',
            component: () => import('@/modules/hr/UserManagement/masters/ruleMaster/components/RuleMasterList.vue'),
            meta: { title: 'Rule Master', requiresAuth: true, breadcrumb: 'Rule Master' }
          },
          {
            path: 'code-template',
            name: 'CodeTemplate',
            component: () => import('@/modules/hr/UserManagement/masters/codeTemplate/components/CodeTemplate.vue'),
            meta: { title: 'Code Template', requiresAuth: true, breadcrumb: 'Code Template' }
          },
          {
            path: 'news-events',
            name: 'NewsEventsList',
            component: () => import('@/modules/hr/UserManagement/masters/newsEvents/components/NewsEventsList.vue'),
            meta: { title: 'News & Events', requiresAuth: true, breadcrumb: 'News & Events' }
          },
          {
            path: 'organisation-config',
            name: 'OrganisationConfig',
            component: () => import('@/modules/hr/UserManagement/masters/organisationConfig/components/OrganisationConfig.vue'),
            meta: { title: 'Organization Config', requiresAuth: true, breadcrumb: 'Organization Config' }
          }
        ]
      },

      // --- Full-page forms (no rail: these are focused create/edit screens) ---
      {
        path: 'location-master/form',
        name: 'LocationMasterForm',
        component: () => import('@/modules/hr/UserManagement/masters/locationMaster/components/LocationMasterForm.vue'),
        meta: { title: 'Location Master Form', requiresAuth: true, breadcrumb: 'Location Master Form' }
      },
      {
        path: 'location-type/form',
        name: 'LocationTypeForm',
        component: () => import('@/modules/hr/UserManagement/masters/locationType/components/LocationTypeForm.vue'),
        meta: { title: 'LocationType Form', requiresAuth: true, breadcrumb: 'LocationType Form' }
      },
      {
        path: 'department/form',
        name: 'DepartmentForm',
        component: () => import('@/modules/hr/UserManagement/masters/department/components/DepartmentForm.vue'),
        meta: { title: 'Department Form', requiresAuth: true, breadcrumb: 'Department Form' }
      },
      {
        path: 'rule-master/form',
        name: 'RuleMasterForm',
        component: () => import('@/modules/hr/UserManagement/masters/ruleMaster/components/RuleMasterForm.vue'),
        meta: { title: 'Rule Master Form', requiresAuth: true, breadcrumb: 'Rule Master Form' }
      },

      // --- Legacy top-level paths -> nested master routes ---
      { path: 'organisation-config', redirect: { name: 'OrganisationConfig' } },
      { path: 'location-master',     redirect: { name: 'LocationMasterList' } },
      { path: 'location-type',       redirect: { name: 'LocationTypeList' } },
      { path: 'code-template',       redirect: { name: 'CodeTemplate' } },
      { path: 'approval',            redirect: { name: 'ApprovalList' } },
      { path: 'rule-master',         redirect: { name: 'RuleMasterList' } },
      { path: 'news-events',         redirect: { name: 'NewsEventsList' } }
    ]
  }
]
